'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { useAdminSession } from '@/contexts/AdminSessionContext';
import Logo from '@/components/Logo';

type Paso = 'credenciales' | 'verificar2fa' | 'setup2fa';

function LoginForm() {
  const { login, completarSetup2FA } = useAdminSession();
  const router    = useRouter();
  const [paso, setPaso]         = useState<Paso>('credenciales');
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [codigo, setCodigo]     = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [secreto, setSecreto]   = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  async function handleSubmitCredenciales(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const result = await login(email, password);
      if (result.status === 'ok') {
        router.replace('/admin');
      } else if (result.status === 'requiere2FA') {
        setPaso('verificar2fa');
      } else if (result.status === 'requiereSetup2FA') {
        setSetupToken(result.setupToken);
        setSecreto(result.secreto);
        setQrDataUrl(result.qrDataUrl);
        setPaso('setup2fa');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerificar2FA(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const result = await login(email, password, codigo);
      if (result.status === 'ok') router.replace('/admin');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSetup2FA(e: React.FormEvent) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await completarSetup2FA(setupToken, codigo);
      router.replace('/admin');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center p-4">
      <div className="absolute top-5 left-5">
        <Link href="/" className="flex items-center gap-2 text-sm text-white/40 hover:text-white/70 transition-colors group">
          <svg className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Volver al inicio
        </Link>
      </div>
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-5">
            <Logo height={56} href="/" className="brightness-[1.8]" />
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
            <span className="text-xs font-semibold text-red-400 uppercase tracking-widest">Panel de Administración</span>
          </div>
          <p className="text-white/30 text-xs">Acceso restringido · Solo personal autorizado</p>
        </div>

        {paso === 'credenciales' && (
          <form onSubmit={handleSubmitCredenciales} className="bg-white/3 border border-white/8 rounded-2xl p-6 space-y-4">
            <div>
              <label className="block text-xs text-white/50 mb-1.5">Email</label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)} required
                autoComplete="off"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-red-500/50"
                placeholder="correo electrónico"
              />
            </div>
            <div>
              <label className="block text-xs text-white/50 mb-1.5">Contraseña</label>
              <input
                type="password" value={password} onChange={e => setPassword(e.target.value)} required
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm text-white focus:outline-none focus:border-red-500/50"
                placeholder="••••••••"
              />
            </div>

            {error && <p className="text-xs text-red-400 bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>}

            <button
              type="submit" disabled={loading}
              className="w-full py-2.5 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {loading ? 'Ingresando...' : 'Ingresar'}
            </button>
          </form>
        )}

        {paso === 'verificar2fa' && (
          <form onSubmit={handleVerificar2FA} className="bg-white/3 border border-white/8 rounded-2xl p-6 space-y-4">
            <div>
              <h2 className="text-sm font-bold text-white mb-1">Verificación en dos pasos</h2>
              <p className="text-xs text-white/40">Ingresá el código de 6 dígitos de tu app de autenticación.</p>
            </div>
            <input
              type="text" inputMode="numeric" maxLength={6} value={codigo}
              onChange={e => setCodigo(e.target.value.replace(/\D/g, ''))}
              autoFocus required
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-center text-2xl tracking-[0.4em] text-white focus:outline-none focus:border-red-500/50"
              placeholder="000000"
            />
            {error && <p className="text-xs text-red-400 bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>}
            <button
              type="submit" disabled={loading || codigo.length !== 6}
              className="w-full py-2.5 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
            <button
              type="button"
              onClick={() => { setPaso('credenciales'); setCodigo(''); setError(''); }}
              className="w-full text-xs text-white/30 hover:text-white/60 transition-colors"
            >
              ← Volver
            </button>
          </form>
        )}

        {paso === 'setup2fa' && (
          <form onSubmit={handleSetup2FA} className="bg-white/3 border border-white/8 rounded-2xl p-6 space-y-4">
            <div>
              <h2 className="text-sm font-bold text-white mb-1">Activación obligatoria de 2FA</h2>
              <p className="text-xs text-white/40">
                Por seguridad, todas las cuentas admin requieren verificación en dos pasos. Escaneá el código con Google Authenticator, Authy o similar.
              </p>
            </div>
            {qrDataUrl && (
              <div className="flex justify-center bg-white p-3 rounded-xl">
                <img src={qrDataUrl} alt="QR 2FA" className="w-40 h-40" />
              </div>
            )}
            <div>
              <p className="text-[11px] text-white/30 mb-1">¿No podés escanear? Ingresá este código manualmente:</p>
              <p className="text-xs font-mono text-white/60 bg-white/5 rounded-lg px-3 py-2 break-all select-all">{secreto}</p>
            </div>
            <input
              type="text" inputMode="numeric" maxLength={6} value={codigo}
              onChange={e => setCodigo(e.target.value.replace(/\D/g, ''))}
              required
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-center text-2xl tracking-[0.4em] text-white focus:outline-none focus:border-red-500/50"
              placeholder="000000"
            />
            {error && <p className="text-xs text-red-400 bg-red-500/10 px-3 py-2 rounded-lg">{error}</p>}
            <button
              type="submit" disabled={loading || codigo.length !== 6}
              className="w-full py-2.5 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-600 transition-colors disabled:opacity-50"
            >
              {loading ? 'Activando...' : 'Activar y continuar'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function AdminLoginPage() {
  return <LoginForm />;
}
